import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
    static classes = ['error', 'loading'];
    static targets = ['form', 'info', 'input', 'layout', 'results', 'toggler'];
    static values = {
        'pageParameter': String,
        'searchParameter': String,
    };

    #abortController = null;
    #currentPage = 1;
    #inputDebounce = null;
    #popStateHandler = null;

    //
    // Lifecycle methods
    //
    initialize() {
        this.#popStateHandler = () => this.#searchFromUrlData(window.location.href, { ignoreFacets: true });
    }

    connect() {
        this.#executeInitialSearch();
        window.addEventListener('popstate', this.#popStateHandler);
    }

    disconnect() {
        clearTimeout(this.#inputDebounce);
        this.#abortRequest();
        window.removeEventListener('popstate', this.#popStateHandler);
    }

    //
    // Public methods
    //
    filter() {
        this.#currentPage = 1;
        this.#executeSearchRequest(this.#createPayload(['buffer.info', 'buffer.results'], true));
        this.#scrollToTop();
    }

    input() {
        this.#currentPage = 1;
        clearTimeout(this.#inputDebounce);
        this.#inputDebounce = setTimeout(() => this.search(), 220);
    }

    paginate(event) {
        // Support fetching URL from:
        // 1. Element's "value" attribute (e.g. <button> or <select>)
        // 2. Element's "href" attribute (e.g. <a>)
        // 3. Element's "data-t42fes-paginate-url-param" attribute (e.g. data-t42fes-paginate-page-param="…")
        let url = event.currentTarget.value || event.currentTarget.href || event.params.url;

        // Support relative URLs
        if (url && !url.startsWith('http')) {
            url = `${window.location.origin}/${url.replace(/^\/+/, '')}`;
        }

        this.#searchFromUrlData(url, { include: ['buffer.info', 'buffer.results'] });
        this.#scrollToTop();
    }

    search() {
        this.#currentPage = 1;
        this.#executeSearchRequest(this.#createPayload(['buffer'], false));
        this.#scrollToTop();
    }

    toggle(event) {
        this.togglerTargets
            .filter(v => v.getAttribute('data-t42fes-toggle') === event.currentTarget.getAttribute('data-t42fes-toggle'))
            .forEach(v => v.disabled = !event.currentTarget.checked)
        ;
    }

    //
    // Private methods
    //
    #abortRequest() {
        if (this.#abortController) {
            this.#abortController.abort();
            this.#abortController = null;
        }
    }

    #createPayload(include, withFilters) {
        const query = this.inputTarget.value.trim();

        if (query === '') {
            return null;
        }

        const payload = new URLSearchParams(new FormData(this.formTarget));
        payload.append('include', include.join(','));

        if (!withFilters) {
            for (const [key, value] of [...payload.entries()]) {
                // Non-filters must be kept (e.g. search config token)
                if (!key.startsWith('filters')) {
                    continue;
                }

                // The search query must be kept
                if ((key === 'filters[0][type]' && value === 'search') || key === 'filters[0][query]') {
                    continue;
                }

                payload.delete(key);
            }
        }

        return payload;
    }

    #executeInitialSearch() {
        if (this.#searchFromUrlData(window.location.href, { ignoreFacets: true })) {
            return;
        }

        this.#executeSearchRequest(this.#createPayload(['buffer'], false));
    }

    #executeSearchRequest(payload, headers = {}) {
        this.#abortRequest();
        this.element.classList.remove(this.errorClass);

        // Just clear the results if no payload is given
        if (payload === null) {
            this.layoutTarget.innerHTML = '';
            this.#syncUrl();
            return;
        }

        this.#abortController = new AbortController();
        this.element.classList.add(this.loadingClass);

        const url = new URL(this.formTarget.action);
        url.searchParams.set(this.pageParameterValue, this.#currentPage);

        fetch(url.toString(), {
            method: 'POST',
            body: payload,
            signal: this.#abortController.signal,
            headers: {
                ...headers,
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        })
            .then(response => {
                if (response.ok) {
                    const searchQuery = response.headers.get('t42-frontend-search-query');

                    if (searchQuery) {
                        this.#syncUrl(searchQuery);
                    }

                    return response.json()
                }

                return Promise.reject(response);
            })
            .then(payload => {
                // No buffer -> remove all elements
                if (!payload?.buffer) {
                    this.layoutTarget.innerHTML = '';
                    return;
                }

                // Replace the whole layout
                if (payload.buffer.layout) {
                    this.layoutTarget.innerHTML = payload.buffer.layout;
                    return;
                }

                const partials = {
                    info: this.infoTarget,
                    results: this.resultsTarget,
                };

                for (const [key, element] of Object.entries(partials)) {
                    if (payload.buffer[key]) {
                        element.outerHTML = payload.buffer[key];
                    }
                }
            })
            .catch(error => {
                if (error?.name === 'AbortError') {
                    return;
                }

                this.element.classList.add(this.errorClass);
            })
            .finally(() => this.element.classList.remove(this.loadingClass));
    }

    #searchFromUrlData(url, { include = [], ignoreFacets = false } = {}) {
        const urlParams = new URL(url);

        if (!urlParams.searchParams.has(this.searchParameterValue)) {
            return false;
        }

        let urlData;

        try {
            urlData = JSON.parse(urlParams.searchParams.get(this.searchParameterValue));
        } catch (e) {
            throw new Error('Search is not a valid JSON');
        }

        if (!urlData) {
            return false;
        }

        const searchFilter = urlData?.filters.find(v => v.type === 'search');

        // Set the query from the URL
        if (searchFilter && searchFilter.query) {
            this.inputTarget.value = searchFilter.query;
        }

        const formData = this.#convertFormDataToObject(new FormData(this.formTarget))

        // Do not change the order! The filters from URL data have the priority
        const payload = {
            ...formData,
            ...urlData,
        };

        if (ignoreFacets && payload.facets && payload.facets.length > 0) {
            payload.facetCountIgnoredFilters = payload.facets.filter(v => v.type === 'count').map(v => v.field);
        }

        if (include.length > 0) {
            payload.include = include.join(',');
        }

        this.#currentPage = urlParams.searchParams.has('page') ? parseInt(urlParams.searchParams.get('page'), 10) : 1;
        this.#currentPage = Math.max(1, this.#currentPage);

        this.#executeSearchRequest(JSON.stringify(payload), {
            'Content-Type': 'application/json'
        });

        return true;
    }

    #convertFormDataToObject(formData) {
        const result = {};

        for (const [key, value] of formData) {
            const parts = key.split(/\[|]\[|]/).filter(Boolean);
            let current = result;

            for (let i = 0; i < parts.length - 1; i++) {
                const part = parts[i];
                const nextPart = parts[i + 1];
                const nextIsIndex = /^\d+$/.test(nextPart);

                if (!(part in current)) {
                    current[part] = nextIsIndex ? [] : {};
                }

                current = current[part];
            }

            const lastPart = parts[parts.length - 1];
            const isArrayIndex = /^\d+$/.test(lastPart);

            if (isArrayIndex) {
                current[parseInt(lastPart, 10)] = value;
            } else {
                current[lastPart] = value;
            }
        }

        return result;
    }

    #scrollToTop() {
        const target = this.element.parentElement;
        const rect = target.getBoundingClientRect();

        if (rect.top < 0) {
            target.scrollIntoView();
        }
    }

    #syncUrl(query) {
        const url = new URL(window.location.href);

        url.search = '';

        if (query) {
            url.searchParams.append(this.searchParameterValue, query);
            url.searchParams.append(this.pageParameterValue, this.#currentPage);
        }

        // Do nothing if the URL doesn't change
        if (url.toString() === window.location.href) {
            return;
        }

        history.pushState({}, '', url);
    }
}
